import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import withErrorGuard from "../../../../HOC/withErrorGuard";
import { getVarsoUrl } from "../../../../common/helpers/helpers";
import HelperText from "../../../../components/atoms/HelperText";
import {
  getSpliceVersion,
  getSpliceTranscripts,
  getSplicePerTranscript,
} from "../../../../state/selectors/spliceAi/selectors";
import { getVariantGeneralInfo } from "../../../../state/selectors/CardsUI.selectors";
import {
  getIsFLT3ITD,
  getACMGAnnotationTranscript,
  getRefGenome,
} from "../../../../state/selectors/general.selectors";
import { truncateHgvs } from "../../../../components/TruncatedText/helpers";
import TruncatedText from "../../../../components/TruncatedText/TruncatedText";
import ProteinValue from "../ProteinValue/ProteinValue";

function Title({ isFLT3ITD, variantGeneralInfo, spliceVersion }) {
  return (
    <div className="tw-flex tw-items-left tw-justify-left tw-gap-x-4">
      <div className="tw-font-bold">
        <a
          href={getVarsoUrl("https://kidsneuro.shinyapps.io/splicevault/")}
          target="_blank"
          rel="noopener noreferrer"
          className="url-title"
        >
          SpliceVault
        </a>
        <HelperText
          RenderContent={() => (
            <span>
              SpliceVault houses the resource of the most common mis-splicing
              events local to the exon-intron junction, based on unannotated
              splice-junctions detected across 335,301 publicly available
              RNA-Seq samples from GTEx and SRA, and built-in GRCh38 (300K-RNA).
              It is collectively hosted with an older version of the database,
              40K-RNA. 300K-RNA (top-4 events) is an empirical method which
              accurately predicts the nature of variant associated mis-splicing
              with 90% sensitivity.{" "}
              <p className="py-1">
                See{" "}
                <a
                  href="https://www.nature.com/articles/s41588-022-01293-8"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Dawes et al. 2023
                </a>{" "}
                for details on formation of the 300K-RNA database.{" "}
              </p>
              <p className="py-1">
                The original results can be viewed in the SpliceVault web
                portal.
              </p>
            </span>
          )}
        />
      </div>
      {!isFLT3ITD && variantGeneralInfo ? (
        <div className="tw-font-bold tw-text-xs tw-flex tw-items-center">
          {variantGeneralInfo?.hgvsCdot ? (
            <span>{variantGeneralInfo.hgvsCdot}</span>
          ) : null}
          {variantGeneralInfo?.pValue ? (
            <span>{variantGeneralInfo.pValue}</span>
          ) : null}
        </div>
      ) : null}
      {isFLT3ITD && variantGeneralInfo ? (
        <span className="tw-font-bold tw-no-underline tw-flex tw-items-center">
          {variantGeneralInfo?.hgvsCdot ? (
            <TruncatedText
              truncatedText={truncateHgvs(variantGeneralInfo?.hgvsCdot)}
              originalText={variantGeneralInfo?.hgvsCdot}
            />
          ) : null}
          {variantGeneralInfo?.pValue ? (
            <ProteinValue
              isFLT3ITD={isFLT3ITD}
              pValue={variantGeneralInfo?.pValue}
            />
          ) : null}
        </span>
      ) : null}
      {spliceVersion ? (
        <div className="tw-flex tw-items-center">
          <small>Version: {spliceVersion}</small>
        </div>
      ) : null}
    </div>
  );
}

Title.propTypes = {
  variantGeneralInfo: PropTypes.shape({
    hgvsCdot: PropTypes.string,
    pValue: PropTypes.string,
  }).isRequired,
  isFLT3ITD: PropTypes.bool,
  spliceVersion: PropTypes.string.isRequired,
  spliceDataPerTranscript: PropTypes.shape({}).isRequired,
};

Title.defaultProps = {
  isFLT3ITD: false,
};

const mapStateToProps = (state) => {
  return {
    refGenome: getRefGenome(state),
    acmgTranscript: getACMGAnnotationTranscript(state),
    variantGeneralInfo: getVariantGeneralInfo(state),
    isFLT3ITD: getIsFLT3ITD(state),
    spliceVersion: getSpliceVersion(state),
    spliceTranscripts: getSpliceTranscripts(state),
    spliceDataPerTranscript: getSplicePerTranscript(state),
  };
};

export default connect(mapStateToProps)(
  withErrorGuard(Title, "SpliceVault Title", "SpliceVault Title")
);
